import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import Pressable from '@/components/base/Pressable';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { updatePassword } = useAuth();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [updated, setUpdated] = useState(false);

  const t = {
    bg: isDark ? 'bg-[#0F0F10]' : 'bg-white',
    textPrimary: isDark ? 'text-[#F5F5F7]' : 'text-neutral-900',
    textSecondary: isDark ? 'text-[#A1A1A6]' : 'text-neutral-500',
    errorBg: isDark ? 'bg-red-500/10 border-red-500/20 text-red-400' : 'bg-red-50 border-red-200 text-red-600',
    successBg: isDark ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-emerald-50 border-emerald-200 text-emerald-600',
  };

  const handleSubmit = useCallback(async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    const { error: updateError } = await updatePassword(password);
    setIsSubmitting(false);
    if (updateError) {
      setError(updateError.message || 'Could not update your password. Please request a new link.');
      return;
    }
    setUpdated(true);
  }, [confirmPassword, password, updatePassword]);

  return (
    <div className={`study-shell study-auth min-h-[100dvh] flex items-center justify-center ${t.bg} px-5 transition-colors duration-500`} data-theme={resolvedTheme}>
      <motion.div
        className="w-full max-w-[380px]"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.32, 0.72, 0, 1] }}
      >
          <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-neutral-900 flex items-center justify-center mb-4">
              <i className="ri-lock-password-line text-white text-xl" />
          </div>
            <h1 className={`text-xl font-bold ${t.textPrimary}`}>Set a New Password</h1>
          <p className={`text-sm ${t.textSecondary} mt-1 text-center`}>
              Choose a new password for your LAVYN account.
          </p>
        </div>

          {error && (
            <div className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-xs mb-4 ${t.errorBg}`}>
              <i className="ri-error-warning-line text-sm" />
              <span>{error}</span>
            </div>
          )}

          {updated ? (
            <div className={`flex flex-col gap-3 px-4 py-4 rounded-xl border text-xs mb-4 ${t.successBg}`}>
              <div className="flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-sm" />
                <span className="font-semibold">Password updated</span>
              </div>
              <p className="opacity-80">Your password has been changed. You can now log in.</p>
              <Pressable
                onClick={() => navigate('/auth/login')}
                className="w-full bg-neutral-900 text-white py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
                scale={0.97}
              >
                <i className="ri-login-box-line text-sm" />
                <span>Go to Log In</span>
              </Pressable>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3.5">
              <div>
                <label className={`block text-xs font-medium ${t.textSecondary} mb-1.5`}>New Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="At least 6 characters"
                  autoComplete="new-password"
                  minLength={6}
                  required
                  className={`w-full ${isDark ? 'bg-white/[0.04]' : 'bg-neutral-50'} border ${isDark ? 'border-white/[0.08]' : 'border-neutral-200'} rounded-xl px-4 py-3 text-sm ${t.textPrimary} outline-none`}
                />
              </div>
              <div>
                <label className={`block text-xs font-medium ${t.textSecondary} mb-1.5`}>Confirm Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder="Re-enter your password"
                  autoComplete="new-password"
                  required
                  className={`w-full ${isDark ? 'bg-white/[0.04]' : 'bg-neutral-50'} border ${isDark ? 'border-white/[0.08]' : 'border-neutral-200'} rounded-xl px-4 py-3 text-sm ${t.textPrimary} outline-none`}
                />
              </div>
              <Pressable
                type="submit"
                disabled={isSubmitting || !password || !confirmPassword}
                className="w-full bg-neutral-900 text-white py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40"
                scale={0.97}
              >
                {isSubmitting ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <i className="ri-save-line text-sm" />
                )}
                <span>{isSubmitting ? 'Updating...' : 'Update Password'}</span>
              </Pressable>
            </form>
          )}

          {!updated && (
            <Pressable
              onClick={() => navigate('/')}
              className={`w-full mt-3 ${isDark ? 'bg-white/[0.06] text-white' : 'bg-neutral-100 text-neutral-900'} py-3.5 rounded-2xl text-sm font-semibold flex items-center justify-center gap-2`}
              scale={0.97}
            >
              <i className="ri-arrow-left-line text-sm" />
              <span>Continue as Guest</span>
            </Pressable>
          )}
      </motion.div>
    </div>
  );
}